import React from 'react';
import { Container } from 'react-bootstrap';
import styled from 'styled-components';

const PolicyWrapper = styled.div`
  padding: 48px 0;
  overflow-y: auto;
  height: 100vh;

  h1 {
    font-size: 2rem;
    margin-bottom: 1.5rem;
  }

  h2 {
    font-size: 1.25rem;
    margin-top: 2rem;
    margin-bottom: 0.75rem;
  }

  p {
    margin-bottom: 1rem;
    color: #333;
    line-height: 1.7;
  }

  ul,
  ol {
    margin-bottom: 1rem;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.5rem;
      line-height: 1.7;
      color: #333;
    }
  }
`;

const TermsOfServicePage = () => (
  <PolicyWrapper>
    <Container>
      <h1>Rambla Terms of Service</h1>

      <p>
        These Rambla Terms of Service (&ldquo;Rambla Terms&rdquo;) govern your access to and use of
        the Rambla services, including the Rambla websites located at <strong>rambla.io</strong> and{' '}
        <strong>ramblas.io</strong>, the Rambla web application, the Rambla mobile application, the
        Rambla brand administration tools, creator tools, and any related software, features, or
        services (collectively, the &ldquo;Rambla Service&rdquo;). By accessing or using the Rambla
        Service, you agree to be bound by these Rambla Terms and the Rambla Privacy Policy (if
        presented or incorporated by reference on the Rambla Service), as each may be updated from
        time to time.
      </p>

      <p>
        <strong>
          IF YOU DO NOT AGREE TO THESE RAMBLA TERMS, YOU MAY NOT ACCESS OR USE THE RAMBLA SERVICE.
        </strong>
      </p>

      <h2>1. Roles and Definitions</h2>
      <ul>
        <li>
          <strong>&ldquo;Brand&rdquo;</strong> means a business or organization using Rambla to
          manage content and distribution.
        </li>
        <li>
          <strong>&ldquo;Brand Admin&rdquo;</strong> means an authorized user acting on behalf of a
          Brand with administrative access to Rambla.
        </li>
        <li>
          <strong>&ldquo;Creator&rdquo;</strong> means an individual invited by a Brand Admin to
          create, publish, or share Brand-approved content using Rambla.
        </li>
        <li>
          <strong>&ldquo;Location Owner/Manager&rdquo;</strong> means a user invited or authorized
          to create or manage location-based or in-person shoppable content using Rambla.
        </li>
        <li>
          <strong>&ldquo;Brand Content&rdquo;</strong> means content, assets, product data, and
          guidelines provided or approved by a Brand through the Rambla Service.
        </li>
        <li>
          <strong>&ldquo;Creator Content&rdquo;</strong> means content created or assembled by a
          Creator using Rambla, which may incorporate Brand Content.
        </li>
        <li>
          <strong>&ldquo;Published Page&rdquo;</strong> means content published through Rambla,
          including on a Brand subdomain on <strong>ramblas.io</strong> or other destinations
          supported by Rambla.
        </li>
      </ul>

      <h2>2. Account Registration and Access</h2>
      <p>
        To use certain features of the Rambla Service, you may be required to create an account and
        maintain current, complete, and accurate information. You are responsible for safeguarding
        your credentials and for all activity that occurs under your account. If you believe your
        account has been compromised, you must notify us promptly through the support contact
        provided on the Rambla Service.
      </p>

      <h2>3. Brand Admin Controls and Creator Invitations</h2>
      <p>
        Brand Admins may invite Creators and other authorized users to access the Rambla Service.
        Brand Admins control which users are invited, what Brand Content is accessible, what
        permissions are granted, and what content may be published. Creators and other invited users
        agree to follow the Brand&apos;s instructions, brand guidelines, and permission settings
        configured through Rambla.
      </p>

      <h2>4. Publishing, Subdomains, and Shoppable Content</h2>
      <p>
        Rambla enables the creation and publication of Brand-approved content for sharing on social
        media and in person. Published Pages may be hosted on a Brand&apos;s subdomain on{' '}
        <strong>ramblas.io</strong> (or other destinations supported by Rambla). Rambla may provide
        tools to create shoppable content, product highlights, and related experiences.
      </p>

      <p>
        <strong>Checkout occurs on the Brand&apos;s domain.</strong> Brands using Rambla control
        where checkout happens, including routing checkout to the Brand&apos;s own ecommerce domain
        (including, where applicable, a Shopify storefront or other third-party checkout). Unless
        expressly stated, Rambla does not process checkout payments and is not the merchant of
        record.
      </p>

      <h2>5. Shopify Integration (Rambla Shopify App)</h2>
      <p>
        Rambla may offer a Shopify application (the &ldquo;Rambla Shopify App&rdquo;) that connects
        a Brand&apos;s Shopify store to a Brand Admin account on Rambla. By installing or using the
        Rambla Shopify App, you acknowledge and agree to the following:
      </p>
      <ol>
        <li>
          <strong>Third-Party Platform.</strong> Shopify is a third-party platform. Your
          relationship with Shopify, including your Shopify store, is governed by Shopify&apos;s
          terms and policies. Rambla does not control Shopify and is not responsible for
          Shopify&apos;s operations or availability.
        </li>
        <li>
          <strong>Authorization and Permissions.</strong> You authorize the Rambla Shopify App to
          access Shopify store data and related resources only to the extent necessary to provide
          Rambla features and functionality, consistent with the permissions you grant during
          installation and as described in the Rambla Privacy Policy.
        </li>
        <li>
          <strong>Data Scope.</strong> Depending on the features enabled, the Rambla Shopify App may
          access store information such as product/catalog data, inventory-related metadata,
          collections, store settings, order-related data (if enabled), and other information
          required for syncing and creating shoppable content.
        </li>
        <li>
          <strong>Merchant Responsibilities.</strong> You represent and warrant that you have the
          authority to install the Rambla Shopify App and to grant access to the Shopify store and
          its data. You are responsible for ensuring your use of Rambla and Shopify complies with
          applicable laws and platform policies.
        </li>
        <li>
          <strong>Checkout and Commerce Terms.</strong> Any purchase completed on a Brand&apos;s
          domain (including via Shopify checkout) is a transaction between the purchaser and the
          Brand (and its payment processor) and is subject to the Brand&apos;s and any applicable
          third-party terms, fees, taxes, refunds, and chargeback policies.
        </li>
        <li>
          <strong>API Dependencies.</strong> Rambla features may depend on Shopify APIs and
          services. Rambla does not warrant uninterrupted integration where interruptions are caused
          by Shopify, third-party services, or API limitations/changes.
        </li>
        <li>
          <strong>Uninstall and Revocation.</strong> You may revoke access by uninstalling the
          Rambla Shopify App or modifying app permissions within Shopify. Certain data may persist
          as described in the Rambla Privacy Policy and/or to comply with legal obligations.
        </li>
      </ol>

      <h2>6. Fees, Free Tier, and Subscriptions</h2>
      <p>
        Rambla is offered as a subscription service for Brands and may be priced on a usage-based
        basis. Rambla may also be made available for free with limits on usage, features, publishing
        volume, invited users, or other constraints (the &ldquo;Free Tier&rdquo;). Plan details,
        usage limits, and pricing (if applicable) will be made available within the Rambla Service
        and may be updated from time to time on a going-forward basis.
      </p>
      <p>
        <strong>No transaction fees.</strong> Rambla does not charge partner fees on checkout
        transactions. Brands may incur separate third-party fees charged by Shopify, payment
        processors, carriers, or other vendors, which are solely the Brand&apos;s responsibility.
      </p>
      <p>
        If you exceed Free Tier limits or plan allowances, Rambla may (at its discretion) restrict
        functionality, limit usage, suspend features, or require you to upgrade to a paid plan to
        continue using certain features.
      </p>

      <h2>7. Acceptable Use and Prohibited Conduct</h2>
      <p>You agree not to, and not to assist others to:</p>
      <ul>
        <li>
          Use Rambla to publish content you are not authorized to publish or that violates Brand
          guidelines or permission settings.
        </li>
        <li>
          Infringe or misappropriate intellectual property, privacy, publicity, or other rights of
          any person or entity.
        </li>
        <li>
          Upload, transmit, or distribute malware or harmful code, or attempt to bypass security
          features.
        </li>
        <li>
          Reverse engineer, decompile, or disassemble the Rambla Service except to the extent such
          restriction is prohibited by law.
        </li>
        <li>
          Use automated scraping or extraction tools except through interfaces expressly provided by
          Rambla.
        </li>
        <li>Engage in fraud, impersonation, or unauthorized access to accounts or systems.</li>
      </ul>

      <h2>8. Content and Intellectual Property</h2>
      <p>
        Brands retain ownership of their Brand Content and applicable rights in their trademarks and
        brand assets. Creators retain ownership of Creator Content to the extent they hold such
        rights, subject to any licenses they grant to Brands and to Rambla for the purpose of
        operating the Rambla Service.
      </p>
      <p>
        You grant Rambla a non-exclusive, worldwide, royalty-free license to host, store, reproduce,
        display, and distribute Brand Content and Creator Content solely as necessary to operate,
        provide, maintain, and improve the Rambla Service and to publish content as directed through
        the Rambla Service.
      </p>

      <h2>9. Privacy and Data</h2>
      <p>
        Rambla&apos;s collection, use, sharing, and processing of personal data and store data
        (including Shopify data where applicable) is described in the Rambla Privacy Policy. You
        agree that Rambla may collect and process information in accordance with that policy and
        applicable law.
      </p>

      <h2>10. Disclaimers</h2>
      <p>
        THE RAMBLA SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo; WITHOUT
        WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, STATUTORY OR OTHERWISE, INCLUDING WITHOUT
        LIMITATION ANY IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
        NON-INFRINGEMENT. RAMBLA DOES NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR
        ERROR-FREE, OR THAT INTEGRATIONS (INCLUDING SHOPIFY) WILL OPERATE WITHOUT INTERRUPTION OR
        CHANGES.
      </p>

      <h2>11. Limitation of Liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT WILL RAMBLA BE LIABLE FOR ANY INDIRECT,
        INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS,
        REVENUE, DATA, GOODWILL, OR BUSINESS INTERRUPTION, ARISING OUT OF OR RELATED TO YOUR USE OF
        (OR INABILITY TO USE) THE RAMBLA SERVICE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH
        DAMAGES.
      </p>
      <p>
        RAMBLA IS NOT RESPONSIBLE FOR ANY THIRD-PARTY CHECKOUT, PAYMENT PROCESSING, TAX CALCULATION,
        REFUND, CHARGEBACK, FULFILLMENT, SHIPPING, OR MERCHANT COMPLIANCE OBLIGATIONS THAT OCCUR ON
        A BRAND&apos;S DOMAIN OR THROUGH THIRD-PARTY PLATFORMS (INCLUDING SHOPIFY AND PAYMENT
        PROCESSORS).
      </p>

      <h2>12. Termination</h2>
      <p>
        Rambla may suspend or terminate your access to the Rambla Service at any time if you violate
        these Rambla Terms, misuse the Rambla Service, or as otherwise necessary to protect Rambla,
        Brands, Creators, or third parties. You may stop using the Rambla Service at any time. Brand
        Admins may remove Creator access at any time.
      </p>

      <h2>13. Changes to These Rambla Terms</h2>
      <p>
        We may update these Rambla Terms from time to time. Changes will be effective when posted on
        the Rambla Service. Your continued use of the Rambla Service after changes become effective
        constitutes acceptance of the updated Rambla Terms.
      </p>

      <h2>14. Contact</h2>
      <p>
        For questions about Rambla, including these Rambla Terms or the Shopify integration, please
        use the support contact information provided within the Rambla Service.
      </p>
    </Container>
  </PolicyWrapper>
);

export default TermsOfServicePage;
